import { AfterViewChecked, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Store } from '@ngrx/store';
import { ProjectAccessRoles, User } from '@completion/models';
import { getCurrentUser, getRole } from '@completion/selectors';
import { MatDialog } from '@angular/material/dialog';
import { UserPreferenceDialogComponent } from '../user-preference-dialog/user-preference-dialog.component';
import { AboutDialogComponent } from '../about-dialog/about-dialog.component';
import { UserManualService } from '../../shared/user-manual/user-manual.service';
import { AadRoleService } from '../../core/services/aad-role.service';
import { UserAvatarService } from '../../core/services/user-avatar.service';
import { GOD_MODE_APPROVAL_PENDING_MESSAGE, GodModeService } from '../../core/services/god-mode.service';
import { NotificationService } from '../../core/services/notification.service';

@Component({
  selector: 'app-user',
  templateUrl: 'user.component.html',
  styleUrls: ['user.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class UserComponent implements OnInit, AfterViewChecked {
  public user: User;
  public usersFirstLetter: string;
  public isOpen = false;
  public manualActive = false;
  public isAdminOrEditor = false;
  public aadRoleLabels: string[] = [];
  public projectRole = '';
  public isSuperDuperAdmin = false;
  public avatarUrl: string | null = null;
  public roleCards: { label: string; description: string; icon: string }[] = [];
  public godModeEligible = false;
  public godModeEnabled = false;
  public godModeBusy = false;
  public godModeManualDialogVisible = false;
  public godModePendingApprovalDialogVisible = false;

  private static readonly ROLE_DESCRIPTIONS: Record<string, string> = {
    ADMIN: 'Global admin access — manage project setup, assign roles across all projects. Does not grant read/write to project data.',
    USER_RW: 'Read/write access to project data when combined with a project access role.',
    'Super Duper Admin': 'Supreme overlord of the entire system 💖 All powers combined!',
    // Project roles
    VENDOR: 'External vendor — limited to assigned scope.',
    READ_ONLY: 'View-only access to project data.',
    COMPANY_SIGN_CERT: 'Can sign certificates on behalf of a company.',
    OPERATOR_MCP: 'Operate and manage MC packages.',
    OPERATOR_CP: 'Operate and manage commissioning packages.',
    MANAGER: 'Manage commissioning packages and has Supplier module access similar to Editor.',
    EDITOR: 'Edit project data — tags, punch, check sheets, etc.',
  };

  constructor(
    private store: Store,
    private changeRef: ChangeDetectorRef,
    private msalService: MsalService,
    private dialog: MatDialog,
    private ums: UserManualService,
    private router: Router,
    private aadRoles: AadRoleService,
    private userAvatar: UserAvatarService,
    private godMode: GodModeService,
    private notifications: NotificationService,
  ) {}

  ngAfterViewChecked(): void {
    this.changeRef.detectChanges();
  }

  ngOnInit(): void {
    console.log(this.aadRoles.getRoles());
    this.store.select(getCurrentUser).subscribe((user: User) => {
      this.user = user;
    });
    this.ums.manualActive$.subscribe((active) => (this.manualActive = active));
    this.userAvatar.avatar$.subscribe((url) => (this.avatarUrl = url));
    this.godMode.eligible$.subscribe((eligible) => (this.godModeEligible = eligible));
    this.godMode.enabled$.subscribe((enabled) => (this.godModeEnabled = enabled));

    // AAD role labels for display
    this.isSuperDuperAdmin = this.aadRoles.isSuperDuperAdmin;
    if (this.isSuperDuperAdmin) {
      this.aadRoleLabels = ['Super Duper Admin'];
    } else {
      this.aadRoleLabels = this.aadRoles.getRoles();
    }

    // Build role cards
    this.store.select(getRole).subscribe((role) => {
      this.isAdminOrEditor = role === ProjectAccessRoles.ADMIN || role === ProjectAccessRoles.EDITOR;
      this.projectRole = role ? role.toString() : '';

      if (this.isSuperDuperAdmin) {
        const allRoles = this.aadRoles.getRoles();
        const projLabel = this.projectRole ? `Project role: ${this.projectRole}` : 'No project role';
        const desc = 'Supreme overlord of the entire system 💖\n\n' + `AAD roles: ${allRoles.join(', ') || '(none)'}\n` + projLabel;
        this.roleCards = [
          {
            label: 'Super Duper Admin',
            description: desc,
            icon: 'shield',
          },
        ];
        return;
      }

      const cards = this.aadRoleLabels.map((r) => ({
        label: r,
        description: UserComponent.ROLE_DESCRIPTIONS[r] ?? 'Application-level role from Azure AD.',
        icon: r === 'ADMIN' ? 'shield' : 'verified_user',
      }));

      if (this.projectRole) {
        cards.push({
          label: 'Project: ' + this.projectRole,
          description: UserComponent.ROLE_DESCRIPTIONS[this.projectRole] ?? 'Project-level access role.',
          icon: 'folder_shared',
        });
      }

      this.roleCards = cards;
    });
  }

  clickOnUserInfo(event): void {
    event.stopPropagation();
  }

  signOut(): void {
    this.godMode.disable();
    this.msalService.logout();
  }

  async toggleGodMode(): Promise<void> {
    if (this.godModeBusy) {
      return;
    }
    if (this.godModeEnabled) {
      this.godMode.disable();
      this.notifications.info('God mode has been disabled.', 'God mode');
      return;
    }

    this.godModeBusy = true;
    try {
      await this.godMode.enable();
      this.isOpen = false;
      this.notifications.warning('God mode is active. It will expire after one minute without mouse movement.', 'God mode enabled', {
        autoCloseMs: 8000,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'AAD re-authentication was cancelled or failed.';
      if (message === GOD_MODE_APPROVAL_PENDING_MESSAGE) {
        this.godModePendingApprovalDialogVisible = true;
        return;
      }
      this.notifications.error(message, 'God mode was not enabled');
    } finally {
      this.godModeBusy = false;
    }
  }

  openUserPreference(): void {
    this.dialog.open(UserPreferenceDialogComponent, {
      width: '700px', // Adjust the width as needed
    });
  }

  openGodModeManual(): void {
    this.godModeManualDialogVisible = true;
  }

  toggleUserManual(): void {
    this.ums.toggleOverlay();
  }

  openAbout(): void {
    this.isOpen = false;
    this.dialog.open(AboutDialogComponent, {
      width: '420px',
    });
  }

  protected openRoadmap() {
    this.router.navigate(['/roadmap']);
  }
}
